import {VIEW_TYPE, EDIT_TYPE, FACE_TYPE, AXIS_TYPE, PLANNING_TYPE, MODULE_TYPE} from '../constants/Type'
import {SVG_MAX_VALUE, SNAP_DIST} from '../constants/App'
import * as SpaceUtil from './Space'
import * as FaceUtil from './Face'

/** 吸着処理ユーティリティ */

/**
 * 吸着可能位置取得
 */
export const getSnapPositions = (space, topSpace, globalSpaceMap, viewType, editType, editFace, viewRotation, viewInverse, snapRule, svgZoomRatio) => {
  if (!space || !Object.keys(space).length || !globalSpaceMap || !viewType || !globalSpaceMap.get(viewType) || !editType || !snapRule) {
    return []
  }
  // 吸着面座標取得
  let faceSnapPositions = getFaceSnapPositions(
    space,
    topSpace,
    globalSpaceMap,
    viewType,
    editType,
    editFace,
    viewRotation,
    viewInverse,
    snapRule,
    svgZoomRatio)
  // 吸着軸座標取得
  let axisSnapPositions = getAxisSnapPositions(
    space,
    topSpace,
    globalSpaceMap,
    viewType,
    editType,
    editFace,
    viewRotation,
    viewInverse,
    snapRule,
    svgZoomRatio)
  let snapPositions = [...faceSnapPositions, ...axisSnapPositions]
  return snapPositions
}

/**
 * ズーム率に合わせて図形吸着距離を取得
 * @param baseSnapDist 図形吸着距離
 * @param svgZoomRatio グラフ表示領域のズーム率
 * @returns {number}
 */
export const getSnapDist = (baseSnapDist, svgZoomRatio) => {
  let snapDist = baseSnapDist * svgZoomRatio / 3
  // 距離が5以下にならないようにする
  if (snapDist < 5) return 5
  return baseSnapDist * svgZoomRatio / 3
}

/**
 * 吸着位置取得
 */
export const getValidSnapPositions = (snapPositions) => {
  if (!snapPositions || !snapPositions.length) {
    return {}
  }
  // 最小吸着距離
  let minDist = {x: null, y: null}
  // 実際に採用する吸着情報
  let positionList = {x: [], y: []}
  // 縦横方向毎に最も吸着距離の小さい吸着情報を取得
  snapPositions.forEach(snapPos => {
    if ([FACE_TYPE.LEFT, FACE_TYPE.RIGHT].includes(snapPos.ownFaceType) || AXIS_TYPE.X === snapPos.ownAxisType) {
      if (minDist.x === null) {
        minDist.x = snapPos.dist
        positionList.x.push(snapPos)
        return
      }
      let tmpDist = Math.abs(snapPos.dist)
      let tmpMin = Math.abs(minDist.x)
      if (tmpDist < tmpMin) positionList.x = []
      if (tmpDist <= tmpMin) positionList.x.push(snapPos)
      return
    }
    if ([FACE_TYPE.BOTTOM, FACE_TYPE.TOP].includes(snapPos.ownFaceType) || AXIS_TYPE.Y === snapPos.ownAxisType) {
      if (minDist.y === null) {
        minDist.y = snapPos.dist
        positionList.y.push(snapPos)
        return
      }
      let tmpDist = Math.abs(snapPos.dist)
      let tmpMin = Math.abs(minDist.y)
      if (tmpDist < tmpMin) positionList.y = []
      if (tmpDist <= tmpMin) positionList.y.push(snapPos)
    }
  })
  return {minDist, positionList}
}

/**
 * 吸着位置取得(面揃え)
 * @return {Object} {ownFaceType: [{tagSpace: SpaceModel, tagFaceType: FACE_TYPE, dist: Number}]}
 */
export const getFaceSnapPositions = (space, topSpace, globalSpaceMap, viewType, editType, editFace, viewRotation, viewInverse, snapRule, svgZoomRatio) => {
  if (!space || !Object.keys(space).length || !globalSpaceMap || !viewType || !globalSpaceMap.get(viewType) || !editType || !snapRule) {
    return []
  }

  // 自空間に関する吸着ルール
  let snapRuleFiltered = {}

  // 自空間のプランニング種別に応じたルール
  if (snapRule.planningTypeKeyMap) {
    let snapRuleByPlanningType = snapRule.planningTypeKeyMap[space.planningType]
    if (snapRuleByPlanningType) {
      snapRuleFiltered = Object.assign({}, snapRuleByPlanningType)
    }
  }
  // 自空間のオブジェクト詳細種別に応じたルール
  if (space.spaceObjectDtlType && snapRule.objDtlTypeKeyMap) {
    let snapRuleByObjDtlType = snapRule.objDtlTypeKeyMap[space.spaceObjectDtlType]
    // マージ
    if (snapRuleByObjDtlType) {
      Object.keys(snapRuleByObjDtlType).forEach(faceType => {
        if (!snapRuleFiltered.hasOwnProperty(faceType)) {
          snapRuleFiltered[faceType] = []
        }
        snapRuleFiltered[faceType].push(...snapRuleByObjDtlType[faceType])
      })
    }
  }
  if (!snapRuleFiltered || !Object.keys(snapRuleFiltered).length) return {}

  // 対象面、軸(2D)
  let targetFaceTypes = []
  switch (editType) {
    case EDIT_TYPE.MOVE:
    case EDIT_TYPE.ADD:
      targetFaceTypes = [FACE_TYPE.RIGHT, FACE_TYPE.LEFT, FACE_TYPE.TOP, FACE_TYPE.BOTTOM]
      break
    case EDIT_TYPE.RESIZE:
      targetFaceTypes = [FaceUtil.convertFaceTypeForDisp(editFace, viewType, viewRotation)]
      break
  }

  // 面によるルール
  let snapRuleByFace = {}
  Object.keys(snapRuleFiltered).filter(ownFaceType => {
    let convertedOwnFace = FaceUtil.convertFaceTypeForDisp(ownFaceType, viewType, viewRotation)
    return targetFaceTypes.includes(convertedOwnFace)
  }).forEach(ownFaceType => {
    snapRuleByFace[ownFaceType] = snapRuleFiltered[ownFaceType]
  })

  // 自空間の面毎に吸着対象空間をフィルタ
  let snappableSpaces = {} // {FACE_TYPE: [{spaceData: {}, tagFace: FACE_TYPE, dist: Number}]}
  let topSpaceGlobal = globalSpaceMap.get(VIEW_TYPE.FRONT).find(s => s.id === topSpace.id)
  Object.keys(snapRuleByFace).forEach(ownFaceType => {
    snappableSpaces[ownFaceType] = []
    snapRuleByFace[ownFaceType].forEach(rule => {
      globalSpaceMap.get(VIEW_TYPE.FRONT)
        .filter(tagSpace => space.id !== tagSpace.id)
        .filter(tagSpace => {
          if (rule.tagPlanningType) {
            return rule.tagPlanningType === tagSpace.planningType
          }
          if (rule.tagObjDtlType) {
            return rule.tagObjDtlType === tagSpace.spaceObjectDtlType
          }
        })
        .forEach(tagSpace => snappableSpaces[ownFaceType].push({spaceData: tagSpace, faceType: rule.tagFaceType}))
    })
    // サーフェス以外は、トップ親空間を吸着対象に含める
    if (PLANNING_TYPE.UNIT_SURFACE !== space.planningType) {
      snappableSpaces[ownFaceType].push({spaceData: topSpaceGlobal, faceType: ownFaceType})
    }
  })

  // 最も近接している空間の面の座標のみ返却
  let snapPositions = []
  let convertedOwnSpace = SpaceUtil.convertSpaceDataForDisp(space, viewType, viewRotation)
  Object.keys(snapRuleByFace).forEach(ownFaceType => {
    let convertedOwnFace = FaceUtil.convertFaceTypeForDisp(ownFaceType, viewType, viewRotation)
    if (!targetFaceTypes.includes(convertedOwnFace)) return

    let tagSpaces = []
    snappableSpaces[ownFaceType].forEach(tagSpace => {
      let ownPos = getFacePosition(space, ownFaceType)
      let tagPos = getFacePosition(tagSpace.spaceData, tagSpace.faceType)
      let tagDist = Math.abs(tagPos - ownPos)
      if (tagDist > getSnapDist(SNAP_DIST, svgZoomRatio)) return // 吸着距離より大きい場合は無視
      tagSpace.dist = tagPos - ownPos
      // 吸着対象がまだなければ無条件で追加
      if (tagSpaces.length === 0) {
        tagSpaces.push(tagSpace)
        return
      }
      // 吸着対象が既にある場合は、より近いものを採用
      let tmpTagDist = Math.abs(tagSpaces[0].dist)
      if (tagDist < tmpTagDist) tagSpaces = []
      if (tagDist <= tmpTagDist) tagSpaces.push(tagSpace)
    })

    // ビューと回転を考慮した、面の線データ変換
    tagSpaces.forEach(tagSpace => {
      let convertedTagSpace = SpaceUtil.convertSpaceDataForDisp(tagSpace.spaceData, viewType, viewRotation)
      let convertedTagFace = FaceUtil.convertFaceTypeForDisp(tagSpace.faceType, viewType, viewRotation)
      snapPositions.push({
        ownSpace: convertedOwnSpace,
        ownFaceType: convertedOwnFace,
        tagSpace: convertedTagSpace,
        tagFaceType: convertedTagFace,
        dist: tagSpace.dist
      })
    })
  })

  return snapPositions
}

/** 面座標取得 */
export const getFacePosition = (space, faceType) => {
  switch (faceType) {
    case FACE_TYPE.LEFT:
      return space.x
    case FACE_TYPE.RIGHT:
      return space.x + space.w
    case FACE_TYPE.BOTTOM:
      return space.y
    case FACE_TYPE.TOP:
      return space.y + space.h
    case FACE_TYPE.BACK:
      return space.z
    case FACE_TYPE.FRONT:
      return space.z + space.d
    default:
  }
  return null
}

/** 面座標取得 */
const getAxisPosition = (space, axisType) => {
  switch (axisType) {
    case AXIS_TYPE.X:
      return space.x + space.w / 2
    case AXIS_TYPE.Y:
      return space.y + space.h / 2
    case AXIS_TYPE.Z:
      return space.z + space.d / 2
    default:
  }
  return null
}

/**
 * 吸着位置取得(中心揃え)
 * @return {Object} {ownFaceType: [{tagSpace, tagFaceType}]}
 */
export const getAxisSnapPositions = (space, topSpace, globalSpaceMap, viewType, editType, editFace, viewRotation, viewInverse, snapRule, svgZoomRatio) => {
  if (!space || !Object.keys(space).length || !globalSpaceMap || !viewType || !globalSpaceMap.get(viewType) || !editType || !snapRule) {
    return []
  }

  // 自空間に関する吸着ルール
  let snapRuleFiltered = {}

  // 自空間のプランニング種別に応じたルール
  if (snapRule.planningTypeKeyMap) {
    let snapRuleByPlanningType = snapRule.planningTypeKeyMap[space.planningType]
    if (snapRuleByPlanningType) {
      snapRuleFiltered = Object.assign({}, snapRuleByPlanningType)
    }
  }
  // 自空間のオブジェクト詳細種別に応じたルール
  if (space.spaceObjectDtlType && snapRule.objDtlTypeKeyMap) {
    let snapRuleByObjDtltype = snapRule.objDtlTypeKeyMap[space.spaceObjectDtlType]
    // マージ
    if (snapRuleByObjDtltype) {
      Object.keys(snapRuleByObjDtltype).forEach(faceType => {
        if (!snapRuleFiltered.hasOwnProperty(faceType)) {
          snapRuleFiltered[faceType] = []
        }
        snapRuleFiltered[faceType].push(...snapRuleByObjDtltype[faceType])
      })
    }
  }
  if (!snapRuleFiltered || !Object.keys(snapRuleFiltered).length || !Object.keys(snapRuleFiltered).includes(FACE_TYPE.UNDEFINED)) {
    return {}
  }

  // 対象面、軸(2D)
  let targetAxisTypes = []
  switch (editType) {
    case EDIT_TYPE.MOVE:
    case EDIT_TYPE.ADD:
      targetAxisTypes = [AXIS_TYPE.X, AXIS_TYPE.Y]
      break
    case EDIT_TYPE.RESIZE:
      return {}
  }

  // 中心揃えによるルール
  let snapRuleByAxis = {}
  snapRuleFiltered[FACE_TYPE.UNDEFINED].forEach(rule => {
    if (!snapRuleByAxis[rule.axisType]) {
      snapRuleByAxis[rule.axisType] = []
    }
    snapRuleByAxis[rule.axisType].push(rule)
  })

  // 自空間の軸毎に吸着対象空間をフィルタ
  let snappableSpaces = {} // {AXIS_TYPE: [{space: {}, tagFace: AXIS_TYPE}]}
  Object.keys(snapRuleByAxis).forEach(ownAxisType => {
    snappableSpaces[ownAxisType] = []
    snapRuleByAxis[ownAxisType].forEach(rule => {
      globalSpaceMap.get(VIEW_TYPE.FRONT)
        .filter(tagSpace => space.id !== tagSpace.id)
        // 棚板は同一階層の棚板と軸吸着はできない
        .filter(tagSpace => {
          if (PLANNING_TYPE.UNIT_SHELF === space.planningType) {
            return space.parentSpaceId !== tagSpace.parentSpaceId
          }
          return true
        })
        // 吸着ルールに応じた種別の空間
        .filter(tagSpace => {
          if (rule.tagPlanningType) return rule.tagPlanningType === tagSpace.planningType
          if (rule.tagObjDtlType) return rule.tagObjDtlType === tagSpace.spaceObjectDtlType
          return false
        })
        .forEach(tagSpace => snappableSpaces[ownAxisType].push({spaceData: tagSpace, axisType: ownAxisType}))
    })
  })

  // 最も近接している空間の軸の座標のみ返却
  let snapPositions = []
  let convertedOwnSpace = SpaceUtil.convertSpaceDataForDisp(space, viewType, viewRotation)
  Object.keys(snapRuleByAxis).forEach(ownAxisType => {
    let convertedOwnAxis = SpaceUtil.convertAxisTypeForDisp(ownAxisType, viewType, viewRotation)
    if (!targetAxisTypes.includes(convertedOwnAxis)) return

    let tagSpaces = []
    snappableSpaces[ownAxisType].forEach(tagSpace => {
      let ownPos = getAxisPosition(space, ownAxisType)
      let tagPos = getAxisPosition(tagSpace.spaceData, tagSpace.axisType)
      let tagDist = Math.abs(ownPos - tagPos)
      if (tagDist > getSnapDist(SNAP_DIST, svgZoomRatio)) return // 吸着距離より大きい場合は無視
      tagSpace.dist = tagPos - ownPos
      if (tagSpaces.length === 0) {
        tagSpaces.push(tagSpace)
        return
      }
      // 吸着対象が既にある場合は、より近いものを採用
      let tmpTagDist = Math.abs(tagSpaces[0].dist)
      if (tagDist < tmpTagDist) tagSpaces = []
      if (tagDist <= tmpTagDist) tagSpaces.push(tagSpace)
    })

    // ビューと回転を考慮した面の線データ変換
    tagSpaces.forEach(tagSpace => {
      let convertedTagSpace = SpaceUtil.convertSpaceDataForDisp(tagSpace.spaceData, viewType, viewRotation)
      let convertedTagAxis = SpaceUtil.convertAxisTypeForDisp(tagSpace.axisType, viewType, viewRotation)
      snapPositions.push({
        ownSpace: convertedOwnSpace,
        ownAxisType: convertedOwnAxis,
        tagSpace: convertedTagSpace,
        tagAxisType: convertedTagAxis,
        dist: tagSpace.dist
      })
    })
  })

  return snapPositions
}

/** 面の線座標取得(2D) */
export const getFaceLine = (rect, faceType, viewInverse) => {
  // ビューの反転を考慮した面
  let converted = faceType
  if (viewInverse.x) {
    switch (converted) {
      case FACE_TYPE.RIGHT: converted = FACE_TYPE.LEFT; break
      case FACE_TYPE.LEFT: converted = FACE_TYPE.RIGHT; break
    }
  }
  if (viewInverse.y) {
    switch (converted) {
      case FACE_TYPE.TOP: converted = FACE_TYPE.BOTTOM; break
      case FACE_TYPE.BOTTOM: converted = FACE_TYPE.TOP; break
    }
  }
  switch (converted) {
    case FACE_TYPE.LEFT:
      return { x1: rect.x, y1: rect.y, x2: rect.x, y2: rect.y + rect.h }
    case FACE_TYPE.RIGHT:
      return { x1: rect.x + rect.w, y1: rect.y, x2: rect.x + rect.w, y2: rect.y + rect.h }
    case FACE_TYPE.BOTTOM:
      return { x1: rect.x, y1: rect.y, x2: rect.x + rect.w, y2: rect.y }
    case FACE_TYPE.TOP:
      return { x1: rect.x, y1: rect.y + rect.h, x2: rect.x + rect.w, y2: rect.y + rect.h }
    default:
  }
  return {}
}

/** 軸の線座標取得(2D) */
export const getAxisLine = (rect, axisType) => {
  switch (axisType) {
    case AXIS_TYPE.X:
      let x = rect.x + rect.w / 2
      return { x1: x, y1: -SVG_MAX_VALUE, x2: x, y2: SVG_MAX_VALUE }
    case AXIS_TYPE.Y:
      let y = rect.y + rect.h / 2
      return { x1: -SVG_MAX_VALUE, y1: y, x2: SVG_MAX_VALUE, y2: y }
    default:
  }
  return {}
}

/**
 * 吸着(モジュール)
 * @param space 空間データ
 * @param prevSpace 編集操作開始前の空間データ
 * @param editType 操作種別
 * @param editFace 面種別
 * @param moduleAtt モジュール
 * @param svgZoomRatio グラフ表示領域のズーム率
 * @returns {{snapped: boolean, spaceData: *}} 補正後の空間データ
 */
export const snapSpaceByModule = (space, prevSpace, editType, editFace, moduleAtt, svgZoomRatio) => {
  let rtnSpace = Object.assign({}, space)
  let rtn = {
    snapped: false,
    spaceData: rtnSpace
  }
  // リサイズのみ
  if (EDIT_TYPE.RESIZE !== editType || !moduleAtt || !Object.keys(moduleAtt).length) return rtn

  let moduleType = null
  let moduleValue = null
  let modulePitch = null
  let moduleFix = []
  let faceAxis = FaceUtil.getVerticalAxis(editFace)
  switch (faceAxis) {
    case AXIS_TYPE.X:
      moduleType = moduleAtt.attValue1
      moduleValue = moduleAtt.attString1
      break
    case AXIS_TYPE.Y:
      moduleType = moduleAtt.attValue3
      moduleValue = moduleAtt.attString2
      break
    case AXIS_TYPE.Z:
      moduleType = moduleAtt.attValue5
      moduleValue = moduleAtt.attString3
      break
    default:
      return rtn
  }
  if (MODULE_TYPE.PITCH.code === moduleType) {
    modulePitch = Number(moduleValue)
  } else if (MODULE_TYPE.FIX.code === moduleType) {
    moduleFix = moduleValue.split('|').map(s => Number(s))
  } else {
    return rtn
  }

  let moduleSnapDist = null
  let sizeDiff = SpaceUtil.getSize(space, faceAxis) - SpaceUtil.getSize(prevSpace, faceAxis)
  // ピッチ
  if (modulePitch && MODULE_TYPE.PITCH.code === moduleType) {
    moduleSnapDist = Math.floor(sizeDiff % modulePitch)
    SpaceUtil.setSize(rtnSpace, faceAxis, SpaceUtil.getSize(space, faceAxis) - moduleSnapDist)
  }
  // 固定値
  if (moduleFix && moduleFix.length && MODULE_TYPE.FIX.code === moduleType) {
    moduleFix.forEach(fixRule => {
      let diff = Math.abs(fixRule - SpaceUtil.getSize(space, faceAxis))
      if (!moduleSnapDist || Math.abs(diff) < Math.abs(moduleSnapDist)) {
        moduleSnapDist = diff
        SpaceUtil.setSize(rtnSpace, faceAxis, fixRule)
      }
    })
  }
  if (moduleSnapDist && Math.abs(moduleSnapDist) < getSnapDist(SNAP_DIST, svgZoomRatio)) {
    if (FaceUtil.isBaseFace(editFace)) {
      let otherFace = FaceUtil.getOtherFace(editFace)
      let otherFacePos = FaceUtil.getFacePosition(prevSpace, otherFace)
      SpaceUtil.setOrigin(rtnSpace, faceAxis, otherFacePos - SpaceUtil.getSize(rtnSpace, faceAxis))
    }
    rtn.snapped = true
  }
  return rtn
}



// WEBPACK FOOTER //
// ./src/main/js/utils/Snap.js